import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DataRequestsState } from '@/types/dataRequestsState';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { PageList } from '@/types/list';
import { CreateClaimSetRequestBody } from '@/domains/claimSets/types/createClaimSet';

export type ClaimSetState = {
  currentClaimSetNumber: string;
  currentClaimSetId: number | undefined;
  claimSetClaimList: PageList;
  dataRequestsStates: {
    createClaimSet: DataRequestsState;
  };

  normalizedV2Response: NormalizedV2Response | null;
  isOverviewDetailsExpanded: boolean;
};

const initialState: ClaimSetState = {
  currentClaimSetNumber: '',
  currentClaimSetId: undefined,
  claimSetClaimList: {
    page: 1,
    perPage: 10,
  },
  dataRequestsStates: {
    createClaimSet: {
      isLoading: false,
      isFetching: false,
      isSuccess: false,
      isError: false,
    },
  },

  normalizedV2Response: null,
  isOverviewDetailsExpanded: false,
};

const claimSetSlice = createSlice({
  name: 'claimSets',
  initialState,
  reducers: {
    initClaimSetPage: () => {},
    setCurrentClaimSetNumber(state, { payload }: PayloadAction<string>) {
      state.currentClaimSetNumber = payload;
    },
    getCurrentClaimSet: () => {},
    setCurrentClaimSetId(state, { payload }: PayloadAction<number>) {
      state.currentClaimSetId = payload;
    },
    incrementClaimSetClaimListPage(state) {
      state.claimSetClaimList.page++;
    },
    resetClaimSetClaimListPage(state) {
      state.claimSetClaimList.page = 1;
    },
    startLoadingCreateClaimSet: (state) => {
      state.dataRequestsStates.createClaimSet = {
        isLoading: true,
        isFetching: !state.dataRequestsStates.createClaimSet,
        isError: false,
        isSuccess: false,
      };
    },
    createClaimSetSuccess(state) {
      state.dataRequestsStates.createClaimSet = {
        isLoading: false,
        isFetching: false,
        isError: false,
        isSuccess: true,
      };
    },
    createClaimSetError(state) {
      state.dataRequestsStates.createClaimSet = {
        isLoading: false,
        isFetching: false,
        isError: true,
        isSuccess: false,
      };
    },
    getClaimSetV2: (_, _action: PayloadAction<string>) => {},
    getClaimSetV2Success: (state, action: PayloadAction<NormalizedV2Response>) => {
      state.normalizedV2Response = action.payload;
    },
    createClaimSet(_, _action: PayloadAction<CreateClaimSetRequestBody>) {},
    setIsOverviewDetailsExpanded(state, action: PayloadAction<boolean>) {
      state.isOverviewDetailsExpanded = action.payload;
    },
    resetClaimSetState: () => initialState,
  },
  selectors: {
    selectCurrentClaimSetId: (state) => state.currentClaimSetId,
    selectCurrentClaimSet: (state) => state.normalizedV2Response,
  },
});

export const {
  getClaimSetV2Success,
  initClaimSetPage,
  setCurrentClaimSetNumber,
  setCurrentClaimSetId,
  getCurrentClaimSet,
  getClaimSetV2,
  createClaimSet,
  startLoadingCreateClaimSet,
  createClaimSetSuccess,
  createClaimSetError,
  setIsOverviewDetailsExpanded,
  resetClaimSetState,
} = claimSetSlice.actions;

export const { selectCurrentClaimSetId, selectCurrentClaimSet } = claimSetSlice.selectors;

export const claimSetsReducer = claimSetSlice.reducer;
export const claimSetsReducerPath = claimSetSlice.reducerPath;
